import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold">Charming Creations</span>
            </Link>
            <p className="text-gray-400 text-sm">
              Handcrafted resin art jewellery and gifts, made with love in Australia.
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <span>🇦🇺</span>
              <span>Australian Made & Owned</span>
            </div>
          </div>
          
          {/* Shop Column */}
          <div>
            <h5 className="font-semibold text-lg mb-4">Shop</h5>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/jewellery" className="hover:text-amber-400 transition-colors">Jewellery</Link></li>
              <li><Link to="/earrings" className="hover:text-amber-400 transition-colors">Earrings</Link></li>
              <li><Link to="/pendants" className="hover:text-amber-400 transition-colors">Pendants</Link></li>
              <li><Link to="/homewares" className="hover:text-amber-400 transition-colors">Homewares</Link></li>
              <li>
                <a 
                  href="https://gifts.charmingcreationsgifts.com/collections/all" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-amber-400 transition-colors"
                >
                  All Products
                </a>
              </li>
            </ul>
          </div>
          
          {/* Company Column */}
          <div>
            <h5 className="font-semibold text-lg mb-4">Company</h5>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/about" className="hover:text-amber-400 transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-amber-400 transition-colors">Contact</Link></li>
              <li><Link to="/faq" className="hover:text-amber-400 transition-colors">FAQ</Link></li>
              <li><Link to="/shipping" className="hover:text-amber-400 transition-colors">Shipping Info</Link></li>
              <li><Link to="/returns" className="hover:text-amber-400 transition-colors">Returns</Link></li>
              <li><Link to="/track-order" className="hover:text-amber-400 transition-colors">Track Order</Link></li>
            </ul>
          </div>
          
          {/* Social Column */}
          <div>
            <h5 className="font-semibold text-lg mb-4">Follow Us</h5>
            <div className="flex space-x-3">
              <a 
                href="https://www.facebook.com/share/1FehrmyNW6/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/charming_creations__gifts?igsh=cmgzdHcxODJ3dHlz&utm_source=qr" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://youtube.com/@charmingcreationsandgifts?si=v-TYkSQGRtXdIiIz" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a 
                href="https://www.tiktok.com/@charmingcreationsgifts" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://www.pinterest.com.au/charmingcreationsgifts" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                aria-label="Pinterest"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0a12 12 0 00-4.37 23.17c-.1-.88-.19-2.24 0-3.2l1.45-6.14s-.37-.74-.37-1.83c0-1.72 1-3 2.24-3 1.05 0 1.56.79 1.56 1.74 0 1.06-.68 2.64-1 4.1-.29 1.21.61 2.2 1.8 2.2 2.16 0 3.83-2.28 3.83-5.57 0-2.91-2.09-4.95-5.08-4.95-3.46 0-5.49 2.6-5.49 5.28 0 1.05.4 2.17.9 2.78a.36.36 0 01.08.35l-.34 1.36c-.05.22-.18.27-.41.16-1.56-.72-2.53-2.99-2.53-4.81 0-3.92 2.85-7.52 8.21-7.52 4.31 0 7.66 3.07 7.66 7.17 0 4.28-2.7 7.73-6.45 7.73-1.26 0-2.45-.66-2.85-1.43l-.78 2.96c-.28 1.08-1.04 2.43-1.55 3.26A12 12 0 1012 0z"/>
                </svg>
              </a>
            </div>
            
            {/* WhatsApp Contact */}
            <div className="mt-6">
              <a
                href="https://wa.me/61415145952?text=Hi%2C%20I%27m%20interested%20in%20your%20handcrafted%20resin%20art%20pieces!"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-green-400 hover:text-green-300 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-gray-800 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Charming Creations. All rights reserved. ABN: Coming Soon
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <Link to="/shipping" className="hover:text-amber-400 transition-colors">Shipping</Link>
              <Link to="/returns" className="hover:text-amber-400 transition-colors">Returns</Link>
              <Link to="/links" className="hover:text-amber-400 transition-colors">Links</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
